import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import {
  aiActivityApi,
  aiActivityQueryKeys,
  conversionRatePct,
  type ActivityEntry,
  type DailyCount,
} from "../../shared/api/aiActivity";
import { apiError } from "../../shared/api/client";
import { StatusPill } from "../../shared/ui/StatusPill";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

/**
 * AI activity dashboard — clinic-owner observability for the Addy AI Assistant
 * (2026 agentic trend item #10). Read-only, PHI-free: counts + 7/30/90-day
 * timeline + recent intake/handoff feed. Pairs with /settings/integrations
 * (connection + scope config) and /settings/audit (raw audit trail).
 */
export function AiActivityPage() {
  const [windowDays, setWindowDays] = useState(7);
  const summary = useQuery({
    queryKey: aiActivityQueryKeys.summary(windowDays),
    queryFn: () => aiActivityApi.summary(windowDays),
    refetchInterval: 30_000,
  });

  if (summary.isLoading) {
    return <div className="p-6 text-muted-foreground">Đang tải hoạt động AI…</div>;
  }
  if (summary.isError || !summary.data) {
    return (
      <div className="p-6 text-destructive">
        Không tải được hoạt động AI: {apiError(summary.error).detail}
      </div>
    );
  }

  const data = summary.data;
  const intakeRate = conversionRatePct(data.intakeDrafts);

  return (
    <div className="max-w-6xl space-y-6 p-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Hoạt động Trợ lý AI
          </h1>
          <p className="text-sm text-muted-foreground">
            Thống kê hoạt động AI trong {data.windowDays} ngày qua. Dữ liệu cập
            nhật mỗi 30 giây.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <WindowSelector value={windowDays} onChange={setWindowDays} />
          <Button asChild variant="link" size="sm" className="px-0">
            <Link to="/settings/integrations">
              Cấu hình kết nối
              <ArrowRight aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </header>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <StatCard
          label="Phiếu tiếp nhận"
          value={data.intakeDrafts.created}
          hint={`${data.intakeDrafts.pending} chờ xử lý`}
        />
        <StatCard
          label="Tỉ lệ chuyển bệnh nhân"
          value={`${intakeRate}%`}
          hint={`${data.intakeDrafts.converted}/${data.intakeDrafts.created} đã chuyển`}
        />
        <StatCard
          label="Phiếu chuyển tiếp"
          value={data.handoffTasks.created}
          hint={`${data.handoffTasks.pending} chờ + ${data.handoffTasks.accepted} đang xử`}
        />
        <StatCard
          label="Đã hoàn tất"
          value={data.handoffTasks.completed}
          hint={`${data.handoffTasks.cancelled} đã hủy`}
        />
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-medium text-foreground">
          Phiếu tiếp nhận theo ngày
        </h2>
        <DailyChart series={data.dailyIntakeCounts} />
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-medium text-foreground">Hoạt động gần nhất</h2>
        <RecentFeed rows={data.recent} />
      </section>
    </div>
  );
}

function WindowSelector({
  value,
  onChange,
}: {
  value: number;
  onChange: (n: number) => void;
}) {
  const options = [7, 30, 90];
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-input">
      {options.map((n) => (
        <Button
          key={n}
          type="button"
          variant={value === n ? "default" : "ghost"}
          size="sm"
          onClick={() => onChange(n)}
          className="rounded-none border-0"
        >
          {n} ngày
        </Button>
      ))}
    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className="mt-1 text-2xl font-semibold text-foreground">{value}</div>
        {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function DailyChart({ series }: { series: DailyCount[] }) {
  if (series.length === 0) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground">
          Chưa có dữ liệu.
        </CardContent>
      </Card>
    );
  }
  const max = Math.max(1, ...series.map((s) => s.count));
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex h-32 items-end gap-2">
          {series.map((s) => {
            const heightPct = Math.round((s.count / max) * 100);
            return (
              <div
                key={s.date}
                className="flex flex-1 flex-col items-center"
                title={`${s.date}: ${s.count}`}
              >
                <div
                  className="w-full rounded-t bg-primary"
                  style={{ height: `${heightPct}%`, minHeight: "2px" }}
                />
                <div className="mt-1 text-[10px] text-muted-foreground">
                  {s.date.slice(5)}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function RecentFeed({ rows }: { rows: ActivityEntry[] }) {
  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground">
          Chưa có hoạt động.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {rows.map((r) => (
        <div key={r.id} className="rounded-lg border bg-card p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-foreground">
              {kindLabel(r.kind)}
            </span>
            <StatusPill tone={statusTone(r.status)}>{r.status}</StatusPill>
          </div>
          <div className="mt-1 text-sm text-muted-foreground">
            Thời gian: {formatTime(r.at)}
          </div>
          <div className="text-sm text-muted-foreground">
            ID: <span className="font-mono text-xs">{r.id.slice(0, 8)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function statusTone(
  status: string
): "neutral" | "success" | "warning" | "danger" | "info" | "primary" {
  switch (status) {
    case "CONVERTED":
    case "COMPLETED":
      return "success";
    case "DISCARDED":
    case "CANCELLED":
      return "danger";
    case "ACCEPTED":
      return "info";
    case "PENDING":
      return "warning";
    default:
      return "neutral";
  }
}

function kindLabel(kind: string): string {
  switch (kind) {
    case "intake_draft":
      return "Phiếu tiếp nhận";
    case "handoff_task":
      return "Phiếu chuyển tiếp";
    default:
      return kind;
  }
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("vi-VN", {
      timeZone: "Asia/Ho_Chi_Minh",
    });
  } catch {
    return iso;
  }
}
